import React, { useMemo, useRef, useState } from "react";
import {
  PrimaryButton,
  DefaultButton,
  Toggle,
  TextField,
  IconButton,
  Spinner,
  MessageBar,
  MessageBarType
} from "@fluentui/react";
import useAxios from "axios-hooks";
import Select from "react-select";
import Toast from "../../../Forms/Toast";

const createNewRow = (cobrandId, key) => ({
  cobrandEmailCourseCompletionId: null,
  cobrandId,
  crsIds: [],
  templateId: "",
  sendCourseIncompleteEmail: false,
  sendCourseCompleteEmail: false,
  clientKey: key
});

const EmailCourseCompletions = () => {
  const [selectedCobrandId, setSelectedCobrandId] = useState(null);
  const [rows, setRows] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const keyRef = useRef(0);
  const selectMenuPortalTarget = typeof window !== "undefined" ? document.body : undefined;

  const [{ data: cobrands, loading: loadingCobrands }] = useAxios({ url: "/internal/cobrands" });
  const cobrandOptions = useMemo(
    () => (cobrands || []).map((c) => ({ value: c.cobrandId, label: c.name })),
    [cobrands]
  );

  const [{ loading: loadingSettings }, fetchSettings] = useAxios(
    { url: "/internal/cobrand/email-course-completions/0" },
    { manual: true }
  );

  const [{ loading: loadingCourses }, fetchCourses] = useAxios(
    { url: "/internal/cobrand/email-course-completions/courses/0" },
    { manual: true }
  );

  const [{ loading: saving }, saveRequest] = useAxios(
    { url: "/internal/cobrand/email-course-completions", method: "POST" },
    { manual: true }
  );

  const resetStateForCobrand = () => {
    setRows([]);
    setAvailableCourses([]);
    setRowErrors({});
    setErrorMessage(null);
    setIsDirty(false);
    keyRef.current = 0;
  };

  const loadCobrandData = async (cobrandId) => {
    if (!cobrandId) {
      resetStateForCobrand();
      return;
    }

    try {
      setErrorMessage(null);
      const [settingsResult, coursesResult] = await Promise.all([
        fetchSettings({ url: `/internal/cobrand/email-course-completions/${cobrandId}` }),
        fetchCourses({ url: `/internal/cobrand/email-course-completions/${cobrandId}/courses` })
      ]);

      const settings = settingsResult?.data || [];
      const courses = coursesResult?.data || [];

      keyRef.current = settings.length;

      setAvailableCourses(
        courses.map((course) => ({
          value: course.crsId,
          label: `${course.title} - Slug: ${course.slug} - CrsId: ${course.crsId} `
        }))
      );

      setRows(
        settings.map((item) => ({
          cobrandEmailCourseCompletionId: item.cobrandEmailCourseCompletionId,
          cobrandId,
          crsIds: item.crsId ? [item.crsId] : [],
          templateId: item.templateId || "",
          sendCourseIncompleteEmail: !!(item.sendCourseIncompleteEmail ?? item.sendIncompleteCourseEmail),
          sendCourseCompleteEmail: !!item.sendCourseCompleteEmail || !!item.templateId,
          clientKey: `existing-${item.cobrandEmailCourseCompletionId}`
        }))
      );
      setRowErrors({});
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      resetStateForCobrand();
      setErrorMessage("Unable to load course completion settings. Please try again.");
    }
  };

  const handleCobrandChange = async (option) => {
    const cobrandId = option ? option.value : null;
    setSelectedCobrandId(cobrandId);
    resetStateForCobrand();
    if (cobrandId) {
      await loadCobrandData(cobrandId);
    }
  };

  const updateRow = (clientKey, changes) => {
    setRows((previous) =>
      previous.map((row) =>
        row.clientKey === clientKey
          ? { ...row, ...changes }
          : row
      )
    );
    setIsDirty(true);
    setRowErrors((prev) => {
      if (!prev[clientKey]) {
        return prev;
      }
      const updated = { ...prev };
      const rowError = { ...updated[clientKey] };
      Object.keys(changes).forEach((field) => {
        if (field in rowError) {
          delete rowError[field];
        }
      });
      if (Object.keys(rowError).length === 0) {
        delete updated[clientKey];
      } else {
        updated[clientKey] = rowError;
      }
      return updated;
    });
  };

  const addRow = () => {
    if (!selectedCobrandId) {
      return;
    }
    keyRef.current += 1;
    const clientKey = `new-${keyRef.current}`;
    setRows((previous) => [...previous, createNewRow(selectedCobrandId, clientKey)]);
    setIsDirty(true);
  };

  const removeRow = (clientKey) => {
    setRows((previous) => previous.filter((row) => row.clientKey !== clientKey));
    setRowErrors((prev) => {
      if (!prev[clientKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[clientKey];
      return updated;
    });
    setIsDirty(true);
  };

  const selectedCourseIds = useMemo(
    () => rows.flatMap((row) => row.crsIds || []).filter((id) => id !== null && id !== undefined),
    [rows]
  );

  const coursesRemaining = useMemo(
    () => availableCourses.filter((course) => !selectedCourseIds.includes(course.value)),
    [availableCourses, selectedCourseIds]
  );

  const getOptionsForRow = (currentRow) => {
    const selectedInOtherRows = rows
      .filter((row) => row.clientKey !== currentRow.clientKey)
      .flatMap((row) => row.crsIds || [])
      .filter((id) => id !== null && id !== undefined);

    return availableCourses.filter(
      (course) => !(selectedInOtherRows.includes(course.value)) || (currentRow.crsIds || []).includes(course.value)
    );
  };

  const validateRows = () => {
    if (!rows.length) {
      setRowErrors({});
      return true;
    }

    const errors = {};
    rows.forEach((row) => {
      const currentErrors = {};
      if (!row.crsIds || row.crsIds.length === 0) {
        currentErrors.crsIds = "Select at least one course.";
      }
      if (row.sendCourseCompleteEmail && !(row.templateId || "").trim()) {
        currentErrors.templateId = "Template ID is required when sending completion email.";
      }
      if (Object.keys(currentErrors).length > 0) {
        errors[row.clientKey] = currentErrors;
      }
    });

    setRowErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!selectedCobrandId) {
      return;
    }

    setErrorMessage(null);
    setShowToast(false);

    if (!validateRows()) {
      return;
    }

    try {
      const payload = {
        cobrandId: selectedCobrandId,
        settings: rows.map((row) => ({
          cobrandEmailCourseCompletionId: row.cobrandEmailCourseCompletionId,
          cobrandId: selectedCobrandId,
          crsIds: row.crsIds,
          templateId: row.sendCourseCompleteEmail ? (row.templateId || "").trim() : null,
          sendCourseCompleteEmail: !!row.sendCourseCompleteEmail,
          sendIncompleteCourseEmail: !!row.sendCourseIncompleteEmail
        }))
      };

      const result = await saveRequest({ data: payload });

      if (result?.error) {
        throw result.error;
      }

      await loadCobrandData(selectedCobrandId);
      setShowToast(true);
    } catch (err) {
      console.error(err);
      setErrorMessage("Unable to save course completion settings. Please try again.");
    }
  };

  const isLoadingRows = loadingSettings || loadingCourses;
  const cobrandSelectValue = cobrandOptions.find((option) => option.value === selectedCobrandId) || null;
  const canAddRow = !!selectedCobrandId;

  return (
    <div style={{ maxWidth: 1100 }}>
      <h2>Email Course Completions</h2>

      <div style={{ maxWidth: 420 }}>
        <Select
          isDisabled={loadingCobrands}
          isClearable
          options={cobrandOptions}
          value={cobrandSelectValue}
          onChange={handleCobrandChange}
          placeholder="Select a cobrand"
        />
      </div>

      {errorMessage && (
        <div style={{ marginTop: 16, maxWidth: 700 }}>
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(null)}>
            {errorMessage}
          </MessageBar>
        </div>
      )}

      {selectedCobrandId && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20, marginBottom: 8 }}>
            <DefaultButton
              text="Add"
              onClick={addRow}
              disabled={saving || isLoadingRows || !canAddRow}
            />
            {selectedCobrandId && !isLoadingRows && coursesRemaining.length === 0 && (
              <span style={{ fontSize: 12, color: "#605e5c" }}>
                All available courses for this cobrand are already configured.
              </span>
            )}
          </div>

          {isLoadingRows ? (
            <Spinner label="Loading settings..." />
          ) : rows.length === 0 ? (
            <p style={{ marginTop: 12 }}>
              No course completion emails configured yet. Use the Add button to create one.
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 20px 8px 12px", borderBottom: "1px solid #edebe9", }}>CRS</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #edebe9" }}>
                      Send Complete Email
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #edebe9", width: 240 }}>
                      Template ID
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #edebe9" }}>
                      Send Incomplete Email
                    </th>
                    <th style={{ width: 48, borderBottom: "1px solid #edebe9" }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const crsError = rowErrors[row.clientKey]?.crsIds;
                    const templateError = rowErrors[row.clientKey]?.templateId;
                    const options = getOptionsForRow(row);
                    const courseValue = (row.crsIds || [])
                      .map((id) => options.find((o) => o.value === id) || availableCourses.find((o) => o.value === id))
                      .filter(Boolean);
                    
                    const allSelected = options.length > 0 && courseValue.length === options.length;
                    const selectAllOption = { value: '__SELECT_ALL__', label: allSelected ? 'Deselect All' : 'Select All' };
                    const optionsWithSelectAll = options.length > 1 ? [selectAllOption, ...options] : options;

                    return (
                      <tr key={row.clientKey} style={{ borderBottom: '1px solid #edebe9' }}>
                        <td style={{ padding: '12px 20px 12px 0px' }}>
                          <div style={{ display: 'inline-flex', justifyContent: 'center', gap: 12 }}>
                            <Select
                              placeholder="Select course(s)"
                              value={courseValue}
                              options={optionsWithSelectAll}
                              isMulti
                              onChange={(selected, actionMeta) => {
                                if (actionMeta.action === 'select-option' && actionMeta.option?.value === '__SELECT_ALL__') {
                                  // Select all courses
                                  updateRow(row.clientKey, {
                                    crsIds: options.map((o) => o.value)
                                  });
                                } else if (actionMeta.action === 'deselect-option' && actionMeta.option?.value === '__SELECT_ALL__') {
                                  // Deselect all courses
                                  updateRow(row.clientKey, {
                                    crsIds: []
                                  });
                                } else {
                                  // Normal selection
                                  const filtered = (selected || []).filter(s => s.value !== '__SELECT_ALL__');
                                  updateRow(row.clientKey, {
                                    crsIds: filtered.map((s) => s.value)
                                  });
                                }
                              }}
                              isClearable
                              menuPortalTarget={selectMenuPortalTarget}
                              styles={{
                                container: (base) => ({
                                  ...base,
                                  minWidth: 400,
                                }),
                                control: (base, state) => ({
                                  ...base,
                                  minWidth: 400,
                                  borderColor: crsError ? '#a4262c' : base.borderColor,
                                  boxShadow: state.isFocused
                                    ? `0 0 0 1px ${crsError ? '#a4262c' : '#605e5c'}`
                                    : crsError
                                      ? '0 0 0 1px #a4262c'
                                      : base.boxShadow,
                                  '&:hover': {
                                    borderColor: crsError ? '#a4262c' : '#605e5c',
                                  },
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 10000,
                                }),
                              }}
                            />
                          </div>
                          {crsError && (
                            <div style={{ color: '#a4262c', fontSize: 12, marginTop: 4 }}>{crsError}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 0', textAlign: 'center' }}>
                          <Toggle
                            checked={!!row.sendCourseCompleteEmail}
                            onText="On"
                            offText="Off"
                            onChange={(_, checked) =>
                              updateRow(row.clientKey, {
                                sendCourseCompleteEmail: !!checked,
                              })
                            }
                          />
                        </td>
                        <td style={{ padding: '12px 20px 12px 0px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', justifyContent: 'center' }}>
                            <TextField
                              styles={{ fieldGroup: { minWidth: 230 } }}
                              value={row.templateId}
                              onChange={(_, newValue) =>
                                updateRow(row.clientKey, { templateId: newValue || '' })
                              }
                              disabled={!row.sendCourseCompleteEmail}
                              placeholder="Template ID"
                              errorMessage={templateError}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '12px 10', textAlign: 'center' }}>
                          <Toggle
                            checked={!!row.sendCourseIncompleteEmail}
                            onText="On"
                            offText="Off"
                            onChange={(_, checked) =>
                              updateRow(row.clientKey, { sendCourseIncompleteEmail: !!checked })
                            }
                          />
                        </td>
                        <td style={{ padding: '12px 0', textAlign: 'center' }}>
                          <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            ariaLabel="Remove"
                            onClick={() => removeRow(row.clientKey)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <PrimaryButton
              text={saving ? "Saving..." : "Save"}
              onClick={handleSave}
              disabled={!selectedCobrandId || saving || isLoadingRows || !isDirty}
            />
          </div>
        </>
      )}

      <Toast show={showToast} message="Course completion settings saved." onClose={() => setShowToast(false)} />
    </div>
  );
};

export default EmailCourseCompletions;









